import { agentCaller } from '../../../../lib/ai/agentCaller';
import { supabase } from '../../../../lib/supabase';

export const executeDataNode = async ({ settings }) => {
  try {
    if (!settings?.selectedTable?.id) {
      throw new Error('No table selected');
    }

    // Get the full table data including current column_order
    const { data: tableData, error: tableError } = await supabase
      .from('data_tables')
      .select('*')
      .eq('id', settings.selectedTable.id)
      .single();

    if (tableError) throw tableError;
    if (!tableData) throw new Error('Table not found');

    // Ensure we have both dataset and column_order
    if (!tableData.dataset) {
      console.warn('No dataset found for table:', settings.selectedTable.id);
      tableData.dataset = [];
    }

    if (!tableData.column_order) {
      console.warn('No column_order found, generating from dataset');
      // Generate column_order from first row of dataset if available
      tableData.column_order = tableData.dataset[0] 
        ? Object.keys(tableData.dataset[0]) 
        : [];
    }

    console.log('Retrieved table data:', {
      name: tableData.name,
      dataset: tableData.dataset,
      column_order: tableData.column_order
    });

    const output = {
      name: tableData.name,
      dataset: tableData.dataset,
      column_order: tableData.column_order
    };

    return {
      success: true,
      output,
      execution: {
        status: 'completed',
        output: output
      }
    };
  } catch (error) {
    console.error('Error executing getData node:', error);
    return {
      success: false,
      error: error.message,
      execution: {
        status: 'failed',
        error: error.message
      }
    };
  }
};

export async function executeUpdateDataNode({
  instructions,
  fullTableData,
  sourceNodeResults,
  selectedTable,
  projectId,
  organizationId,
  userId
}) {
  console.log("--------- UPDATE DATA NODE EXECUTION ---------");


  
  try {
    if (!selectedTable?.id) {
      throw new Error('No table selected or invalid table ID');
    }

    if (!fullTableData?.dataset) {
      throw new Error('No table data available');
    }

    if (Object.keys(sourceNodeResults).length === 0) {
      throw new Error('No valid data found in source node results');
    }

    // Format source datasets section
    const sourceDatasets = Object.entries(sourceNodeResults)
      .map(([nodeId, data]) => (
        `${data}`
      ))
      .join('\n\n');

      console.log('Source node results:', sourceDatasets);

    // CALL AN AGENT TO UPDATE THE DATA
    const sPrompt = 'You are a data specialist that takes a JSON array of objects and updates them based on instructions and new data. You must maintain the structure and format of the existing data while applying the updates.';
    const uPrompt = `Update this dataset based on these instructions: ${instructions}

Current dataset to update:
${JSON.stringify(fullTableData.dataset, null, 2)}

Source datasets to use:
${sourceDatasets}

Rules:
1. Output must be a valid JSON array of objects
2. Maintain the same structure as the current dataset
3. Use appropriate data types (string, number, boolean)
4. Use snake_case for property names
5. Do not change the order of existing objects
6. Only update objects that match the criteria in the instructions
7. Do not include any other comments or instructions in the output`;

    const payload = {
      userPrompt: uPrompt,
      systemPrompt: sPrompt,
      agentName: 'dave',
      projectId,
      organizationId,
      currentUserId: userId,
      outputFormat: 'json'
    };

    const agentResult = await agentCaller(payload);

    if (!agentResult.success) {
      return {
        success: false,
        error: agentResult.error,
      };
    }

    let updatedDataset = processJsonOutput(agentResult.output);
    
    // Validate the dataset structure
    if (!Array.isArray(updatedDataset)) {
      console.error('Invalid dataset structure:', updatedDataset);
      throw new Error('Agent did not return a valid array of objects');
    }

    // Update the data_tables entry
    const { data: updatedTable, error: updateError } = await supabase
      .from('data_tables')
      .update({ 
        dataset: updatedDataset,
        updated_by: userId,
        updated_at: new Date().toISOString()
      })
      .eq('id', selectedTable.id)
      .select()
      .single();

    if (updateError) {
      console.error('Error updating table:', updateError);
      throw updateError;
    }

    console.log('Table updated successfully:', updatedTable);
    
    return {
      success: true,
      output: updatedTable,
      message: 'Data table updated successfully'
    };

  } catch (error) {
    console.error('Error executing update data node:', error);
    throw error;
  }
} 

export async function executeCreateDataNode({
  tableName,
  instructions,
  folder_id,
  sourceNodeResults,
  projectId,
  organizationId,
  userId
}) {
  console.log("--------- CREATE DATA NODE EXECUTION ---------");
  
  if (!tableName) {
    throw new Error('No table name provided');
  }

  try {
    if (Object.keys(sourceNodeResults).length === 0) {
      throw new Error('No valid data found in source node results');
    }

    // Format source datasets section
    const sourceDatasets = Object.entries(sourceNodeResults)
      .map(([nodeId, data]) => (
        `${data}`
      ))
      .join('\n\n');

    console.log('Source node results:', sourceDatasets);

    // CALL AN AGENT TO CREATE THE DATA STRUCTURE
    const sPrompt = 'You are a data specialist that takes instructions and creates a JSON array of objects based on those instructions. You must ensure the data structure is consistent across all objects and follows database best practices.';
    const uPrompt = `Create a JSON array of objects based on these instructions: ${instructions}

Source datasets to use:
${sourceDatasets}

Rules:
1. Output must be a valid JSON array of objects
2. All objects must have the same structure
3. Use appropriate data types (string, number, boolean)
4. Use snake_case for property names
5. Include at least one example object if no data can be extracted
6. Preserve the original data structure where appropriate
7. Do not include any other comments or instructions in the output
8. Each object must be a flat structure with single-level key-value pairs only (no nested objects or arrays)
9. Example of valid structure:
   [
     {
       "first_name": "John",
       "age": 25,
       "is_active": true
     }
   ]
10. Example of invalid nested structure:
    [
      {
        "user": {
          "name": "John"
        }
      }
    ]
`;

    const payload = {
      userPrompt: uPrompt,
      systemPrompt: sPrompt,
      agentName: 'dave',
      projectId,
      organizationId,
      currentUserId: userId,
      outputFormat: 'json'
    };

    const agentResult = await agentCaller(payload);

    if (!agentResult.success) {
      return {
        success: false,
        error: agentResult.error,
      };
    }

    let dataset = processJsonOutput(agentResult.output);
    
    // Validate the dataset structure
    if (!Array.isArray(dataset)) {
      console.error('Invalid dataset structure:', dataset);
      throw new Error('Agent did not return a valid array of objects');
    }

    // Create the data_tables entry
    const { data: newTable, error: createError } = await supabase
      .from('data_tables')
      .insert({ 
        name: tableName,
        dataset: dataset,
        project_id: projectId,
        folder_id: folder_id,
        created_by: userId,
        updated_by: userId,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        column_order: Object.keys(dataset[0] || {})
      })
      .select()
      .single();

    if (createError) {
      console.error('Error creating table:', createError);
      throw createError;
    }

    console.log('Table created successfully:', newTable);
    
    return {
      success: true,
      output: newTable,
      message: 'Data table created successfully'
    };

  } catch (error) {
    console.error('Error executing create data node:', error);
    throw error;
  }
}

const processJsonOutput = (data) => {

  try {
      if (typeof data === 'string') {
        let cleanedString = data.replace(/```json\n|\n```$/g, '');
        cleanedString = cleanedString.replace(/[\r\t]/g, '');
        data = JSON.parse(cleanedString);
      } else if (Array.isArray(data) || (typeof data === 'object' && data !== null)) {

          console.log('here')
        data = data;
      }
  } catch (e) {
      console.warn('Failed to parse JSON response:', e);
      const cleanedError = data.replace(/```json\n|\n```$|[\r\t]/g, '');
      data = `{"error": "Failed to parse JSON", "originalResponse": "${cleanedError}"}`;
  }

  return data;
}
